import React, { useState } from 'react';
import { X, AlertCircle, ChevronDown, ChevronUp, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * Enhanced Bulk Upload Preview Component
 * Shows detailed preview with old→new value comparison for duplicates
 */
const BulkUploadPreview = ({
  isOpen,
  onClose,
  previewData,
  onConfirm,
  moduleName
}) => {
  const [showAllNew, setShowAllNew] = useState(false);
  const [showAllDuplicates, setShowAllDuplicates] = useState(false);

  if (!isOpen || !previewData) return null;

  const {
    new_records = [],
    duplicates = [],
    duplicate_count = 0,
    errors = []
  } = previewData;

  const displayNewRecords = showAllNew ? new_records : new_records.slice(0, 10);
  const displayDuplicates = showAllDuplicates ? duplicates : duplicates.slice(0, 10);

  // Get field names based on module type
  const getFieldsForModule = (record) => {
    const commonFields = ['student_name', 'date'];
    
    if (moduleName === 'Attendance') {
      return [...commonFields, 'status', 'notes'];
    } else if (moduleName === 'Health') {
      return [...commonFields, 'status'];
    } else if (moduleName === 'Activities') {
      return [...commonFields, 'activity_type', 'description'];
    } else if (moduleName === 'Results') {
      return ['student_name', 'exam_type', ...Object.keys(record).filter(k => k.includes('_marks'))];
    }
    
    return commonFields;
  };

  const formatFieldName = (field) => {
    return field
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  const renderValueComparison = (oldValue, newValue) => {
    const hasChange = oldValue !== newValue;
    
    return (
      <div className="flex items-center gap-2">
        <span className={`px-2 py-1 rounded ${hasChange ? 'bg-red-50 text-red-700 line-through' : 'text-gray-600'}`}>
          {oldValue || '—'}
        </span>
        {hasChange && (
          <>
            <span className="text-gray-400">→</span>
            <span className="px-2 py-1 rounded bg-green-50 text-green-700 font-medium">
              {newValue || '—'}
            </span>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-2xl font-bold" style={{ color: '#2C5F7F' }}>
                Upload Preview - {moduleName}
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Review changes before confirming upload
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Summary Stats */}
          <div className="grid grid-cols-3 gap-4 mt-6">
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <div>
                  <p className="text-sm text-gray-600">New Records</p>
                  <p className="text-2xl font-bold text-green-600">{new_records.length}</p>
                </div>
              </div>
            </div>

            {duplicate_count > 0 && (
              <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-yellow-600" />
                  <div>
                    <p className="text-sm text-gray-600">Duplicates Found</p>
                    <p className="text-2xl font-bold text-yellow-600">{duplicate_count}</p>
                  </div>
                </div>
              </div>
            )}

            {errors.length > 0 && (
              <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                <div className="flex items-center gap-2">
                  <XCircle className="w-5 h-5 text-red-600" />
                  <div>
                    <p className="text-sm text-gray-600">Errors</p>
                    <p className="text-2xl font-bold text-red-600">{errors.length}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* New Records Section */}
          {new_records.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-semibold text-gray-800">
                  New Records ({new_records.length})
                </h4>
                {new_records.length > 10 && (
                  <button
                    onClick={() => setShowAllNew(!showAllNew)}
                    className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                  >
                    {showAllNew ? (
                      <>
                        <ChevronUp className="w-4 h-4" />
                        Show Less
                      </>
                    ) : (
                      <>
                        <ChevronDown className="w-4 h-4" />
                        Show All ({new_records.length})
                      </>
                    )}
                  </button>
                )}
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-green-100 border-b border-green-200">
                      <tr>
                        {displayNewRecords[0] && getFieldsForModule(displayNewRecords[0]).map(field => (
                          <th key={field} className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase">
                            {formatFieldName(field)}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-green-200">
                      {displayNewRecords.map((record, idx) => (
                        <tr key={idx} className="hover:bg-green-100">
                          {getFieldsForModule(record).map(field => (
                            <td key={field} className="px-4 py-2 text-gray-700">
                              {record[field] || '—'}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Duplicates Section with Old→New Comparison */}
          {duplicate_count > 0 && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-semibold text-gray-800">
                  Duplicate Records - Changes Preview ({duplicate_count})
                </h4>
                {duplicates.length > 10 && (
                  <button
                    onClick={() => setShowAllDuplicates(!showAllDuplicates)}
                    className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                  >
                    {showAllDuplicates ? (
                      <>
                        <ChevronUp className="w-4 h-4" />
                        Show Less
                      </>
                    ) : (
                      <>
                        <ChevronDown className="w-4 h-4" />
                        Show All ({duplicates.length})
                      </>
                    )}
                  </button>
                )}
              </div>

              <div className="space-y-4">
                {displayDuplicates.map((record, idx) => (
                  <div key={idx} className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <AlertCircle className="w-4 h-4 text-yellow-600" />
                      <span className="font-medium text-gray-800">
                        {record.student_name} - {record.date}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-4 ml-6">
                      {getFieldsForModule(record)
                        .filter(field => !['student_name', 'date'].includes(field))
                        .map(field => (
                          <div key={field}>
                            <p className="text-xs text-gray-600 mb-1">{formatFieldName(field)}:</p>
                            {renderValueComparison(
                              record[`existing_${field}`] || record[field],
                              record[field]
                            )}
                          </div>
                        ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-semibold mb-1">Action Required:</p>
                    <p>Choose how to handle these {duplicate_count} duplicate records:</p>
                    <ul className="list-disc list-inside mt-2 space-y-1 ml-4">
                      <li><strong>Overwrite:</strong> Replace old values with new values shown above</li>
                      <li><strong>Skip:</strong> Keep existing data, ignore new values</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Errors Section */}
          {errors.length > 0 && (
            <div>
              <h4 className="text-lg font-semibold text-gray-800 mb-4">
                Validation Errors ({errors.length})
              </h4>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-2">
                {errors.slice(0, 10).map((error, idx) => (
                  <div key={idx} className="flex items-start gap-2 text-sm text-red-700">
                    <XCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>{error}</span>
                  </div>
                ))}
                {errors.length > 10 && (
                  <p className="text-sm text-red-600 mt-2">
                    ... and {errors.length - 10} more errors
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          {duplicate_count > 0 ? (
            <div className="flex items-center justify-end gap-4">
              <Button
                onClick={onClose}
                className="bg-gray-500 hover:bg-gray-600 text-white"
              >
                Cancel
              </Button>
              <Button
                onClick={() => onConfirm('skip')}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                Skip Duplicates ({duplicate_count})
              </Button>
              <Button
                onClick={() => onConfirm('overwrite')}
                className="bg-yellow-600 hover:bg-yellow-700 text-white"
              >
                Overwrite Duplicates ({duplicate_count})
              </Button>
            </div>
          ) : (
            <div className="flex items-center justify-end gap-4">
              <Button
                onClick={onClose}
                className="bg-gray-500 hover:bg-gray-600 text-white"
              >
                Cancel
              </Button>
              <Button
                onClick={() => onConfirm('skip')}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                Confirm Upload ({new_records.length} records)
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkUploadPreview;
